import { Persistor } from 'redux-persist/es/types';

import {
  DEFAULT_DASHBOARD_LOGO_HEIGHT,
  DEFAULT_DASHBOARD_LOGO_URL,
  DEFAULT_DASHBOARD_MENU_ITEM_ACTIVE_BG,
  DEFAULT_DASHBOARD_MENU_ITEM_SELECTED_BG,
  DEFAULT_DASHBOARD_MENU_ITEM_SELECTED_COLOR,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
} from '../config/Constants';
import { DashboardConfig } from '../models/DashboardConfig';
import { Permission } from '../models/Permission';
import { User } from '../models/User';
import AuthAction from '../redux/actions/authActions/Actions';
import DashboardConfigAction from '../redux/actions/dashboardConfigActions/Action';
import { store } from '../redux/reducers';
import { AuthService } from '../services/AuthService';

export const hasRoutePermissions = (
  permissions: string | string[],
  currentUser: any
) => {
  if (currentUser.id === 0) {
    return false;
  }

  const currentPermissions: Permission[] = currentUser.permissions || [];

  if (typeof permissions === 'string') {
    permissions = [permissions];
  }

  if (permissions.length === 0) {
    return true;
  } else {
    let check = 0;

    for (let permission of permissions) {
      permission = permission.trim().toUpperCase();

      if (
        currentPermissions.find((currentPermission: Permission) => {
          return currentPermission.code === permission;
        })
      ) {
        check++;
      }
    }

    return check === permissions.length;
  }
};

export const hasRequiredUserTypes = (
  userTypes: string | string[],
  currentUser: User
) => {
  if (!currentUser?.id || currentUser.id === 0) {
    return false;
  }

  const required = Array.isArray(userTypes) ? userTypes : [userTypes];
  if (required.length === 0) {
    return true;
  }

  return required.includes(currentUser.userType);
};

/**
 * Verifica si el path actual está autorizado por el árbol de menús del usuario
 * (match exacto o por prefijo, p. ej. /portal-go/trainings/:id).
 */
export const userHasMenuAccessForPath = (
  currentUser: User,
  targetPath: string
) => {
  if (!currentUser?.id || currentUser.id === 0) {
    return false;
  }

  const normalizedTarget = (targetPath || '').toLowerCase();
  if (!normalizedTarget) {
    return false;
  }

  const resolveNodePath = (node: {
    menuTypeId?: string | number;
    code?: string;
    redirectLink?: string | null;
  }): string | null => {
    const menuType = String(node.menuTypeId || '');
    switch (menuType) {
      case 'IFRAME_VIEWER':
        return `/dynamic-viewer/${node.code}`;
      case 'POWER_BI_REPORT':
        return `/pbi-viewer/${node.code}`;
      case 'PDF_VIEWER':
        return `/pdf-viewer/${node.code}`;
      case 'OFFICE_VIEWER':
        return `/office-viewer/${node.code}`;
      case 'URL_REDIRECT':
        return `/download/${node.code}`;
      case 'PARENT_MENU':
        return null;
      case 'STANDARD_MENU':
      default:
        return node.redirectLink || null;
    }
  };

  let matched = false;

  const walk = (nodes: any[] = []) => {
    for (const node of nodes) {
      const nodePath = (resolveNodePath(node) || '').toLowerCase();
      if (nodePath) {
        const isExact = nodePath === normalizedTarget;
        const isPrefix = normalizedTarget.startsWith(`${nodePath}/`);
        if (isExact || isPrefix) {
          matched = true;
          return;
        }
      }
      if (node.children?.length) {
        walk(node.children);
        if (matched) return;
      }
    }
  };

  walk((currentUser.menus as any[]) || []);
  return matched;
};

export const getFormattedUserData = (userData: any): User => {
  return {
    id: userData.id,
    username: userData.username,
    userType: userData.userType,
    name: userData.name,
    lastname: userData.lastname,
    email: userData.email,
    dialCode: userData.dialCode,
    phone: userData.phone || null,
    avatar: userData.avatar || null,
    createdAt: userData.createdAt,
    activatedAt: userData.activatedAt || null,
    disabledAt: userData.disabledAt || null,
    token: userData.sessionToken,
    permissions: userData.permissions
      ? userData.permissions.map((permission: any) => {
          return {
            code: permission.code,
            name: permission.name,
            description: permission.description,
            createdAt: permission.createdAt,
          };
        })
      : [],
    roles: userData.roles
      ? userData.roles.map((role: any) => {
          return {
            id: role.id,
            name: role.name,
            description: role.description,
            createdAt: role.createdAt,
          };
        })
      : [],
    menus: userData.menuNodes,
    configSettings: userData.configSettings,
    company: {
      id: userData.company.id,
      name: userData.company.name,
      createdAt: userData.company.createdAt,
      updatedAt: userData.company.userType,
      countryCode: userData.company.countryCode,
      isPrivate: userData.company.isPrivate,
      country: userData.company.country,
      kitIntegrationEnabled: userData.company.kitIntegrationEnabled,
      kitCompanies: userData.company.kitCompanies,
      tiendaPerfectaIntegrationEnabled:
        userData.company.tiendaPerfectaIntegrationEnabled ?? false,
      tiendaPerfectaCompanyCode:
        userData.company.tiendaPerfectaCompanyCode ?? null,
      tiendaPerfectaCompany: userData.company.tiendaPerfectaCompany ?? null,
      additionalConfig: {
        webConfiguration: {
          primaryColor:
            userData.company.additionalConfig.webConfiguration?.primaryColor ||
            null,
          secondaryColor:
            userData.company.additionalConfig.webConfiguration
              ?.secondaryColor || null,
          logo: {
            url:
              userData.company.additionalConfig.webConfiguration?.logo?.url ||
              null,
            height:
              parseFloat(
                `${userData.company.additionalConfig.webConfiguration?.logo?.height}`
              ) || null,
          },
          menu: {
            itemSelectedColor:
              userData.company.additionalConfig.webConfiguration?.menu
                ?.itemSelectedColor || null,
            itemSelectedBg:
              userData.company.additionalConfig.webConfiguration?.menu
                ?.itemSelectedBg || null,
            itemActiveBg:
              userData.company.additionalConfig.webConfiguration?.menu
                ?.itemActiveBg || null,
          },
        },
      },
    },
    restrictedToCountries: userData.restrictedToCountries || false, //TODO: falta
    allowedCountries: userData.restrictedToCountries || [],
  };
};

export const getFormattedDashboardConfig = (user: User): DashboardConfig => {
  return {
    primaryColor:
      user.company.additionalConfig.webConfiguration.primaryColor ||
      DEFAULT_PRIMARY_COLOR,
    secondaryColor:
      user.company.additionalConfig.webConfiguration.secondaryColor ||
      DEFAULT_SECONDARY_COLOR,
    logoUrl:
      user.company.additionalConfig.webConfiguration.logo.url ||
      DEFAULT_DASHBOARD_LOGO_URL,
    logoHeight:
      user.company.additionalConfig.webConfiguration.logo.height ||
      DEFAULT_DASHBOARD_LOGO_HEIGHT,
    menuItemSelectedBg:
      user.company.additionalConfig.webConfiguration.menu.itemSelectedBg ||
      DEFAULT_DASHBOARD_MENU_ITEM_SELECTED_BG,
    menuItemActiveBg:
      user.company.additionalConfig.webConfiguration.menu.itemActiveBg ||
      DEFAULT_DASHBOARD_MENU_ITEM_ACTIVE_BG,
    menuItemSelectedColor:
      user.company.additionalConfig.webConfiguration.menu.itemSelectedColor ||
      DEFAULT_DASHBOARD_MENU_ITEM_SELECTED_COLOR,
  };
};

export const rehydrateAndVerify = async (persistor: Persistor) => {
  try {
    const token = store.getState().auth?.token;

    if (token) {
      const verifyUserSessionResponse = await AuthService.verifySession(token);

      if (verifyUserSessionResponse.success) {
        const currentUser = verifyUserSessionResponse.data as User;
        store.dispatch(AuthAction.signIn(currentUser));
        const dashboardConfig = getFormattedDashboardConfig(currentUser);
        store.dispatch(DashboardConfigAction.save(dashboardConfig));
      } else {
        persistor.purge();
        store.dispatch(AuthAction.signOut({}));
        store.dispatch(DashboardConfigAction.remove());
      }
    }
  } catch (error) {
    console.error('Error verificando sesión:', error);
    persistor.purge();
    store.dispatch(AuthAction.signOut({}));
    store.dispatch(DashboardConfigAction.remove());
  }
};
